import { Component, Inject, OnInit } from '@angular/core';
import { AnsweredExam } from '../../../../../../models/interfaces/answered_exam.interface';
import { StudentGrade } from '../../../../../../models/interfaces/student_grade.interface';
import { firstValueFrom } from 'rxjs';
import { cloneDeep, debounce } from 'lodash-es';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../core/services/toast/toast.service';
import { UtilsService } from '../../../../../../shared/utils/utils.service';
import { ModalService } from '../../../../../../core/services/modal/modal.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-modal-view-exam-answers-as-admin',
    imports: [
        CommonModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        InfiniteScrollModule
    ],
    templateUrl: './modal-view-exam-answers-as-admin.component.html',
    styleUrl: './modal-view-exam-answers-as-admin.component.scss'
})
export class ModalViewExamAnswersAsAdminComponent implements OnInit {
  isLoadingGrades = true;
  examId!: string;
  examName!: string;
  totalQuestions: number = 0;
  requiredQuestions: number = 0;
  students!: Array<StudentGrade>;
  filteredStudents!: Array<StudentGrade>;
  isSearchingStudent = false;
  page = -1;
  pageSize = 20;
  studentsLimit = 0;
  infiniteScrollDisabled = false;
  disableInfiniteCauseFilter = false;
  studentToSearch = '';
  isDownloadingAnswers = false;

  private debouncedSearchStudent = debounce(() => {
    this.filterStudentGradeRemotely();
  }, 1500);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalViewExamAnswersAsAdminComponent>,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private modal: ModalService
  ) {}

  ngOnInit(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    if (!receivedData || !receivedData.examName) return this.closeModal();

    this.examId = receivedData.examId;
    this.examName = receivedData.examName;
    this.totalQuestions = receivedData.totalQuestions;
    this.requiredQuestions = receivedData.requiredQuestions;
    this.fetchExamGrades();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  setDisableInfinite(): void {
    this.infiniteScrollDisabled = this.students.length >= this.studentsLimit || this.disableInfiniteCauseFilter;
  }

  onScrollDown(): void {
    this.fetchExamGrades();
  }

  async fetchExamGrades(): Promise<void> {
    try {
      const params = {
        page: ++this.page,
        pageSize: this.pageSize,
        examId: this.examId
      };
      const response = await firstValueFrom(this.api.fetchExamGrades(params));

      if (params.page === 0) {
        this.students = response.students.rows;
        this.filteredStudents = cloneDeep(this.students);
        this.studentsLimit = response.students.count;
      } else {
        if (this.students.length === this.filteredStudents.length) {
          const newRows = cloneDeep(response.students.rows);
          this.filteredStudents.push(...newRows);
        }
        this.students.push(...response.students.rows);
      }
      this.setDisableInfinite();
      this.isLoadingGrades = false;
    } catch (errorResponse: any) {
      this.isLoadingGrades = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error buscando las calificaciones de este examen');
    }
  }

  filterStudent(event: any): void {
    this.studentToSearch = event?.target?.value ? event.target.value.trim().toLowerCase() : null;
    if (!this.utils.hasSomething(this.studentToSearch)) {
      this.debouncedSearchStudent();
      this.isSearchingStudent = false;
      this.filteredStudents = cloneDeep(this.students);
      this.disableInfiniteCauseFilter = false;
      this.setDisableInfinite();
      return;
    }

    this.isSearchingStudent = true;
    this.debouncedSearchStudent();
  }

  async filterStudentGradeRemotely(): Promise<void> {
    const defaultError = 'Hubo un error buscando estudiante';
    try {
      if (!this.utils.hasSomething(this.studentToSearch)) return;

      const params = {
        examId: this.examId,
        text: this.studentToSearch
      };
      const response = await firstValueFrom(this.api.filterExamGrade(params));
      this.isSearchingStudent = false;
      this.filteredStudents = response.students?.length ? response.students : [];
      this.disableInfiniteCauseFilter = true;
      this.setDisableInfinite();
    } catch (errorResponse: any) {
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || defaultError);
    }
  }

  async openViewExamAnswersModal(student: StudentGrade): Promise<void> {
    const answeredExams: Array<AnsweredExam> = student.answers.map((answer) => ({
      answeredExamId: answer.answeredExamId,
      approved: answer.approved,
      date: answer.date,
      grade: answer.grade,
      answers: answer.answers,
      correctAnswers: answer.correctAnswers
    }));
    await this.modal.openViewExamAnswersModal(this.examName, answeredExams, this.totalQuestions, this.requiredQuestions, student.fullname);
  }

  async downloadStudentsAnswers(): Promise<void> {
    try {
      this.isDownloadingAnswers = true;
      const params = { examId: this.examId };
      await this.api.downloadStudentsAnswers(params);
      this.isDownloadingAnswers = false;
    } catch (errorResponse: any) {
      this.isDownloadingAnswers = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error descargando respuestas de todos');
    }
  }
}
